import type {ReactElement} from 'react';
import type {Component} from 'grapesjs';
import {FORM_MAKER_FONT_OPTIONS, findFontOption, setComponentFontFamily} from '@FormMakerReact/lib/form-maker-style-library';
import {useComponentStyleProperty} from '@FormMakerReact/lib/form-maker-style-hooks';
import {Text, TextSize} from '@Components/text/text';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {FontControlProps, FontOptionRowRenderArgs} from './font-control.types';
import {FONT_PROPERTY} from './font-control.types';
import controlStyles from '@FormMakerReact/layout/form-maker-right-panel/controls/control-row.module.scss';
import styles from './font-control.module.scss';

export function FontControl({component}: FontControlProps): ReactElement {
  const fullFamily = useComponentStyleProperty(component, FONT_PROPERTY);
  const selected = findFontOption(fullFamily);
  const displayName = selected?.name ?? window.i18next.t('pmwjs_form_maker_control_font_default');
  return (
    <div className={controlStyles.row}>
      <Text val={window.i18next.t('pmwjs_form_maker_control_font_label')} size={TextSize.MD} className={controlStyles.label} />
      <div className={controlStyles.value}>{renderFontDropdown(component, displayName, selected?.value ?? null)}</div>
    </div>
  );
}

function renderFontDropdown(component: Component, displayName: string, selectedValue: string | null): ReactElement {
  return (
    <Dropdown
      selector={
        <button type="button" className={styles.fontValue}>
          <Text val={displayName} size={TextSize.MD} className={styles.fontName} />
          <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
        </button>
      }
      popup={
        <ul className={styles.fontList} role="listbox">
          {FORM_MAKER_FONT_OPTIONS.map((option) =>
            renderFontOptionRow({
              option,
              isSelected: option.value === selectedValue,
              onSelect: (): void => {
                setComponentFontFamily(component, option.value);
              },
            })
          )}
        </ul>
      }
      popupClassName={styles.fontPopup}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      position={DROPDOWN_POSITION.FIXED}
      popUpHasCustomWidth
      usePortal
    />
  );
}

function renderFontOptionRow({option, isSelected, onSelect}: FontOptionRowRenderArgs): ReactElement {
  return (
    <li key={option.value} role="option" aria-selected={isSelected}>
      <button type="button" className={`${styles.fontListItem} ${isSelected ? styles.fontListItemSelected : ''}`} style={{fontFamily: option.value}} onClick={onSelect}>
        <Text val={option.name} size={TextSize.MD} className={styles.fontListItemText} />
        {renderSelectedCheck(isSelected)}
      </button>
    </li>
  );
}

function renderSelectedCheck(isSelected: boolean): ReactElement | null {
  if (!isSelected) {
    return null;
  }
  return <Icon icon="icon-check" size={IconSize.SIZE_ICON_16} />;
}
